import { z } from 'zod';
/**
 * The public half of a health data provider (ADR-019): what it is called,
 * what it brings, how its consent screen is reached and how its webhook
 * introduces itself. No secrets and no I/O, so the API can build a consent
 * URL and answer a handshake, the web app can draw a card, and the sync
 * agent can add the private half (token exchange, refresh, pulling data).
 *
 * On-device sources (HealthKit, Health Connect) are not providers: the phone
 * pushes to POST /sync/health when the app is open, and `manual` is a hand.
 */
export const connectableProviderSchema = z.enum(['strava', 'withings', 'garmin']);
export const providerSummarySchema = z.object({
    key: connectableProviderSchema,
    label: z.string(),
    brings: z.array(z.enum(['activities', 'weight', 'body_composition', 'sleep', 'steps'])),
    /** False until the server has a client id for it. */
    configured: z.boolean(),
});
// ---------------------------------------------------------------- Strava --
const STRAVA_OAUTH = 'https://www.strava.com/oauth';
export const stravaDescriptor = {
    key: 'strava',
    label: 'Strava',
    brings: ['activities'],
    // Only what we need: activities, read only. No profile write, no upload.
    scopes: ['activity:read_all'],
    authorizeUrl(clientId, redirectUri, state) {
        const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: 'code',
            approval_prompt: 'auto',
            scope: 'activity:read_all',
            state,
        });
        return `${STRAVA_OAUTH}/authorize?${params.toString()}`;
    },
    webhook: {
        /** Strava's handshake: echo the challenge back when the verify token is ours. */
        verify(query, secret) {
            if (!secret || query['hub.mode'] !== 'subscribe' || query['hub.verify_token'] !== secret) {
                return null;
            }
            const challenge = query['hub.challenge'];
            return challenge ? { 'hub.challenge': challenge } : null;
        },
        /** An activity created or updated; deletions and athlete events ask for nothing. */
        toEvents(body) {
            const event = body;
            if (event.object_type !== 'activity' || event.aspect_type === 'delete' || event.owner_id == null) {
                return [];
            }
            return [{ externalId: String(event.owner_id), hint: { activityId: String(event.object_id) } }];
        },
    },
};
// -------------------------------------------------------------- Withings --
const WITHINGS_ACCOUNT = 'https://account.withings.com';
/**
 * Withings scales, and later their watches and sleep mat. Weight and body
 * composition arrive through the `measure` endpoint; the Notify service
 * posts a form-encoded event naming the Withings user and a time window.
 * Withings checks a callback URL with a HEAD request and expects a plain 200,
 * so the handshake has nothing to verify and says yes.
 */
export const withingsDescriptor = {
    key: 'withings',
    label: 'Withings',
    brings: ['weight', 'body_composition', 'steps', 'sleep'],
    scopes: ['user.metrics', 'user.activity'],
    authorizeUrl(clientId, redirectUri, state) {
        const params = new URLSearchParams({
            response_type: 'code',
            client_id: clientId,
            state,
            scope: 'user.metrics,user.activity',
            redirect_uri: redirectUri,
        });
        return `${WITHINGS_ACCOUNT}/oauth2_user/authorize2?${params.toString()}`;
    },
    webhook: {
        verify() {
            return { ok: true };
        },
        toEvents(body) {
            const event = body;
            if (event.userid == null)
                return [];
            return [
                {
                    externalId: String(event.userid),
                    hint: {
                        appli: event.appli == null ? null : Number(event.appli),
                        startdate: event.startdate == null ? null : Number(event.startdate),
                        enddate: event.enddate == null ? null : Number(event.enddate),
                    },
                },
            ];
        },
    },
};
/** Every provider the platform knows how to connect. A module per entry, in the sync agent. */
export const PROVIDER_DESCRIPTORS = [stravaDescriptor, withingsDescriptor];
export function providerDescriptor(key) {
    return PROVIDER_DESCRIPTORS.find((p) => p.key === key) ?? null;
}
/**
 * Secrets are named by provider: PROVIDER_STRAVA_CLIENT_ID and so on. The
 * older STRAVA_* names keep working for one release; the caller warns.
 */
export function providerEnvNames(key) {
    const upper = key.toUpperCase();
    return {
        clientId: `PROVIDER_${upper}_CLIENT_ID`,
        clientSecret: `PROVIDER_${upper}_CLIENT_SECRET`,
        webhookSecret: `PROVIDER_${upper}_WEBHOOK_SECRET`,
        legacy: key === 'strava'
            ? {
                clientId: 'STRAVA_CLIENT_ID',
                clientSecret: 'STRAVA_CLIENT_SECRET',
                webhookSecret: 'STRAVA_WEBHOOK_VERIFY_TOKEN',
            }
            : null,
    };
}
export function readProviderSecrets(key, env = process.env) {
    const names = providerEnvNames(key);
    const pick = (modern, old) => {
        if (env[modern])
            return { value: env[modern], legacy: false };
        if (old && env[old])
            return { value: env[old], legacy: true };
        return { value: null, legacy: false };
    };
    const id = pick(names.clientId, names.legacy?.clientId);
    const secret = pick(names.clientSecret, names.legacy?.clientSecret);
    const webhook = pick(names.webhookSecret, names.legacy?.webhookSecret);
    return {
        clientId: id.value,
        clientSecret: secret.value,
        webhookSecret: webhook.value,
        legacy: id.legacy || secret.legacy || webhook.legacy,
    };
}
//# sourceMappingURL=providers.js.map